import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useAuth } from '../../../auth/components/AuthProvider';

const CATEGORIAS = [
  { id: 'accidente', label: 'Accidente',  emoji: '🚨', color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)'   },
  { id: 'corte',     label: 'Corte vial', emoji: '🚧', color: '#f97316', bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.3)'  },
  { id: 'obra',      label: 'Obra vial',  emoji: '🏗️', color: '#eab308', bg: 'rgba(234,179,8,0.1)',  border: 'rgba(234,179,8,0.3)'   },
  { id: 'alerta',    label: 'Alerta',     emoji: '⚠️', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)'  },
  { id: 'info',      label: 'Informativo',emoji: 'ℹ️', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.3)'  },
  { id: 'otro',      label: 'Otro',       emoji: '📢', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)',  border: 'rgba(139,92,246,0.3)'  },
];

const EMOJIS = ['🚨','🚧','🏗️','⚠️','ℹ️','🚗','🛣️','🚦','🚔','🛑','📢','🌧️','❄️','💨'];
const FORM_EMPTY = { titulo: '', resumen: '', categoria: 'info', zona: '', icono: 'ℹ️', destacada: false, activo: true };

function fmtDate(val) {
  if (!val) return '—';
  const d = val?.toDate?.() ?? new Date(val);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function CategoriaBadge({ id }) {
  const c = CATEGORIAS.find(x => x.id === id);
  if (!c) return null;
  return (
    <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.color }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold">
      {c.emoji} {c.label}
    </span>
  );
}

/* ── Modal crear / editar ─────────────────────────────────────────── */
function ModalNoticia({ noticia, onClose, onSaved, adminUid }) {
  const isNew = !noticia?.id;
  const [form, setForm] = useState(isNew ? { ...FORM_EMPTY } : {
    titulo:    noticia.titulo    || '',
    resumen:   noticia.resumen   || '',
    categoria: noticia.categoria || 'info',
    zona:      noticia.zona      || '',
    icono:     noticia.icono     || 'ℹ️',
    destacada: noticia.destacada ?? false,
    activo:    noticia.activo    ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.titulo.trim())  { setError('El título es obligatorio.'); return; }
    if (!form.resumen.trim()) { setError('El resumen es obligatorio.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        titulo:       form.titulo.trim(),
        resumen:      form.resumen.trim(),
        categoria:    form.categoria,
        zona:         form.zona.trim(),
        icono:        form.icono.trim() || '📢',
        destacada:    form.destacada,
        activo:       form.activo,
        actualizadoEn: serverTimestamp(),
      };
      if (isNew) {
        payload.creadoEn = serverTimestamp();
        if (adminUid) payload.creadoPor = adminUid;
        await addDoc(collection(db, 'noticias_transito'), payload);
      } else {
        await updateDoc(doc(db, 'noticias_transito', noticia.id), payload);
      }
      onSaved(); onClose();
    } catch (e) { setError('Error al guardar: ' + e.message); setSaving(false); }
  };

  const cat = CATEGORIAS.find(c => c.id === form.categoria) || CATEGORIAS[0];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/60" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-base font-black text-gray-900">
              {isNew ? 'Nueva noticia de tránsito' : 'Editar noticia'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          </div>
          <div className="p-6 space-y-4">

            {/* Categoría */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Categoría</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIAS.map(c => (
                  <button key={c.id} type="button" onClick={() => { set('categoria', c.id); set('icono', c.emoji); }}
                    style={form.categoria === c.id ? { background: c.bg, borderColor: c.border, color: c.color } : {}}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
                      form.categoria === c.id ? '' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}>
                    {c.emoji} {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Ícono */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Ícono</label>
              <div className="flex gap-2 items-center">
                <input type="text" value={form.icono} onChange={e => set('icono', e.target.value)} maxLength={8}
                  className="w-16 text-center text-2xl border border-gray-300 rounded-xl px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300" />
                <div className="flex flex-wrap gap-1.5">
                  {EMOJIS.map(e => (
                    <button key={e} type="button" onClick={() => set('icono', e)}
                      className={`text-lg p-1 rounded-lg ${form.icono === e ? 'bg-orange-100 ring-2 ring-orange-300' : 'hover:bg-gray-100'}`}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Título */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Título</label>
              <input type="text" value={form.titulo} onChange={e => set('titulo', e.target.value)} maxLength={100}
                placeholder="Ej: Corte de tránsito en Av. Libertador Bernardo O'Higgins"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
            </div>

            {/* Resumen */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">
                Resumen <span className="font-normal normal-case text-gray-300 ml-1">{form.resumen.length}/200</span>
              </label>
              <textarea value={form.resumen} onChange={e => set('resumen', e.target.value)} rows={3} maxLength={200}
                placeholder="Descripción breve visible en la app..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300" />
            </div>

            {/* Zona */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Zona / Sector</label>
              <input type="text" value={form.zona} onChange={e => set('zona', e.target.value)} maxLength={80}
                placeholder="Ej: Santiago Centro, Providencia, Maipú..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
            </div>

            {/* Flags */}
            <div className="flex gap-3">
              {[
                { key: 'activo',    label: 'Publicada',  on: 'bg-green-500', cls: 'border-green-200 bg-green-50 text-green-700' },
                { key: 'destacada', label: 'Destacada',  on: 'bg-amber-500', cls: 'border-amber-200 bg-amber-50 text-amber-700' },
              ].map(f => (
                <button key={f.key} type="button" onClick={() => set(f.key, !form[f.key])}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold transition-all ${
                    form[f.key] ? f.cls : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
                  <span className={`w-3 h-3 rounded-full ${form[f.key] ? f.on : 'bg-gray-300'}`} />
                  {f.label}
                </button>
              ))}
            </div>

            {error && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">{error}</div>}

            <div className="flex gap-3 pt-1">
              <button onClick={onClose} className="flex-1 border border-gray-300 rounded-xl py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                style={{ background: cat.color }}
                className="flex-1 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-50 hover:opacity-90">
                {saving ? 'Guardando…' : isNew ? 'Crear noticia' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ─────────────────────────────────────────── */
export default function AdminNoticiasTransito() {
  const { user, rol } = useAuth();
  const [noticias, setNoticias]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [filtrocat, setFiltrocat]   = useState('');
  const [modalEditar, setModalEditar] = useState(null);
  const [eliminando, setEliminando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'noticias_transito'), orderBy('creadoEn', 'desc')));
      setNoticias(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      try {
        const snap = await getDocs(collection(db, 'noticias_transito'));
        setNoticias(snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.creadoEn?.seconds ?? 0) - (a.creadoEn?.seconds ?? 0)));
      } catch (e) { console.error(e); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleToggle = async (n, field) => {
    await updateDoc(doc(db, 'noticias_transito', n.id), { [field]: !n[field], actualizadoEn: serverTimestamp() });
    setNoticias(prev => prev.map(x => x.id === n.id ? { ...x, [field]: !x[field] } : x));
  };

  const handleEliminar = async (n) => {
    if (!window.confirm(`¿Eliminar "${n.titulo}"?`)) return;
    setEliminando(n.id);
    try { await deleteDoc(doc(db, 'noticias_transito', n.id)); setNoticias(p => p.filter(x => x.id !== n.id)); }
    catch (e) { alert('Error: ' + e.message); }
    finally { setEliminando(null); }
  };

  const filtradas = filtrocat ? noticias.filter(n => n.categoria === filtrocat) : noticias;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={filtrocat} onChange={e => setFiltrocat(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300">
            <option value="">Todas las categorías</option>
            {CATEGORIAS.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
          </select>
          <span className="text-xs text-gray-400">{filtradas.length} noticias</span>
        </div>
        <div className="flex gap-2">
          <button onClick={cargar} className="text-xs text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">Actualizar</button>
          <button onClick={() => setModalEditar({})}
            className="bg-orange-600 text-white px-4 py-1.5 rounded-xl text-sm font-bold hover:bg-orange-700">
            + Nueva noticia
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400">Cargando…</div>
      ) : filtradas.length === 0 ? (
        <div className="py-12 text-center space-y-2">
          <p className="text-4xl">🚦</p>
          <p className="text-gray-500">No hay noticias de tránsito.</p>
          <button onClick={() => setModalEditar({})} className="text-orange-600 font-semibold text-sm hover:underline">Crear la primera →</button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtradas.map(n => {
            const cat = CATEGORIAS.find(c => c.id === n.categoria) || CATEGORIAS[5];
            return (
              <div key={n.id} className={`bg-white rounded-2xl border overflow-hidden transition-shadow hover:shadow-sm ${!n.activo ? 'opacity-60' : ''}`}
                style={{ borderLeftWidth: 3, borderLeftColor: cat.color }}>
                <div className="px-4 py-3 flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0 mt-0.5">{n.icono || cat.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <CategoriaBadge id={n.categoria} />
                      {n.zona && <span className="text-[10px] text-gray-400 font-semibold">📍 {n.zona}</span>}
                      {n.destacada && <span className="text-[10px] font-black text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">⭐ Destacada</span>}
                      {!n.activo && <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">Inactiva</span>}
                    </div>
                    <p className="font-bold text-gray-900 text-sm leading-tight">{n.titulo}</p>
                    {n.resumen && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.resumen}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtDate(n.creadoEn)}</span>
                    <button onClick={() => handleToggle(n, 'activo')}
                      className={`w-8 h-4 rounded-full relative transition-colors ${n.activo ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${n.activo ? 'left-4' : 'left-0.5'}`} />
                    </button>
                    <button onClick={() => setModalEditar(n)} className="text-xs font-semibold text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-2.5 py-1">Editar</button>
                    <button onClick={() => handleEliminar(n)} disabled={eliminando === n.id}
                      className="text-xs font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-2.5 py-1 disabled:opacity-40">
                      {eliminando === n.id ? '…' : '✕'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalEditar !== null && (
        <ModalNoticia
          noticia={modalEditar?.id ? modalEditar : null}
          onClose={() => setModalEditar(null)}
          onSaved={cargar}
          adminUid={user?.uid}
        />
      )}
    </div>
  );
}
